import { ReactElement, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import CabinetPage from '../../components/Common/CabinetPage';

import UserPreferences from './Scheduling';
import Account from './Account';
import Subscription from './BillingAndUsers';
// import Organization from './Organization';
import AnalyticsTabContainer from './AnalyticsTab';
import CRMSettingsContainer from './CRM';
import SupportMapContainer from './SupportMap';
import IntegrationSettings from '../IntegrationSettings';
import ManageCalendars from '../ManageCalendars';
import { SubscriptionDetails, User } from '../../store';
import { BILLING_INTERVAL, PAGE_URL, TIER } from '../../constants';
import { CabIcon } from '@CabComponents';
import { Snackbar } from '@mui/material';
import { Organization as OrganizationType } from "../../store";
import { IoLockClosedOutline, IoMenu } from 'react-icons/io5';
import { hasCRM } from '../../utils/featureUtils';
import { CabButton } from '@CabComponents/CabButton';
import { CAB_UI_EVENT } from '../../uiEvents';
import {
  LogoutActions,
  LogoutContent,
  NavLabelContent,
  PaddedSettingsContent,
  SettingsBreadcrumb,
  SettingsBreadcrumbButton,
  SettingsBreadcrumbCurrent,
  SettingsBreadcrumbSeparator,
  SettingsHomeList,
  SettingsHomeRow,
  SettingsHomeRowArrow,
  SettingsHomeRowDescription,
  SettingsHomeRowTitle,
  SettingsContent,
  SettingsLayout,
  SettingsMenuButton,
  SettingsPageDescription,
  SettingsPageTitle,
  SettingsSectionHeader,
  SettingsSectionTabs,
  SettingsSubNavButton,
  SettingsSubNavButtonLabel,
  SettingsTopBar,
} from './Settings.styles';

type SettingsChildTab = {
  label: string;
  description?: string;
  path: string;
  subTab?: string;
  pageKey?: string;
};

const schedulingSubTabs: SettingsChildTab[] = [
  {
    label: 'Meeting Defaults',
    description: 'Set default meeting details, holds, availability, and invite body behavior.',
    path: PAGE_URL.PREFERENCES,
    pageKey: 'preferences',
  },
  {
    label: 'Calendar View',
    description: 'Update how your calendar is displayed in Cabinet.',
    path: `${PAGE_URL.PREFERENCES}/calendar-defaults`,
    subTab: 'calendar-defaults',
    pageKey: 'preferences',
  },
  {
    label: 'Locations',
    description: 'Add and edit locations that can appear in calendar invites.',
    path: `${PAGE_URL.PREFERENCES}/custom-locations`,
    subTab: 'custom-locations',
    pageKey: 'preferences',
  },
  {
    label: 'Templates',
    description: 'Create reusable titles and descriptions for new meetings.',
    path: `${PAGE_URL.PREFERENCES}/saved-templates`,
    subTab: 'saved-templates',
    pageKey: 'preferences',
  },
  {
    label: 'Notifications',
    description: 'Manage scheduling notifications and reminder behavior.',
    path: `${PAGE_URL.PREFERENCES}/notifications`,
    subTab: 'notifications',
    pageKey: 'preferences',
  },
];

const teamSubTabs = [
  {
    label: 'Org Details',
    description: 'Review organization profile details and administrator information.',
    path: PAGE_URL.MY_TEAM,
    pageKey: 'team',
  },
  {
    label: 'Users',
    description: 'Manage users, license tiers, and organization permissions.',
    path: `${PAGE_URL.MY_TEAM}/users`,
    subTab: 'users',
    pageKey: 'team',
  },
  {
    label: 'Subscription',
    description: 'Review subscription details, plans, billing interval, and seats.',
    path: `${PAGE_URL.MY_TEAM}/subscription`,
    subTab: 'subscription',
    pageKey: 'team',
  },
];

const personalOrganizationSubTabs = [
  { label: 'Personal', path: PAGE_URL.ANALYTICS_TAB },
  { label: 'Organization', path: `${PAGE_URL.ANALYTICS_TAB}/org`, subTab: 'org' },
];

const crmSubTabs = [
  { label: 'Personal', path: PAGE_URL.CRM_SETTINGS },
  { label: 'Organization', path: `${PAGE_URL.CRM_SETTINGS}/org`, subTab: 'org' },
];

const schedulingTitleBySubTab: Record<string, string> = {
  'calendar-defaults': 'Calendar View',
  'custom-locations': 'Locations',
  'saved-templates': 'Templates',
  notifications: 'Notifications',
};

const schedulingDescriptionBySubTab: Record<string, string> = {
  'calendar-defaults': 'Update how the calendar is displayed in Cabinet.',
  'custom-locations': 'Add and edit locations that will appear in calendar invites sent through your calendar ' +
    'provider via Cabinet.',
};

const teamTitleBySubTab: Record<string, string> = {
  users: 'Users',
  subscription: 'Subscription',
};

type SettingsNavItem = {
  label: ReactNode;
  title: string;
  description: string;
  path?: string;
  key: string;
  disabled?: boolean;
  hidden?: boolean;
  onClick?: () => void;
  children?: SettingsChildTab[];
};

interface Props {
  subPage?: string;
  subTab?: string;
  user: User | null | undefined;
  organization: OrganizationType | null;
  paymentButtonDisabled: boolean;
  paymentDialogError: string;
  paymentDialogMessage: string;
  paymentRedirectDialogOpen: boolean;
  toastMessage: string;
  clearPaymentDialogError: () => void;
  onCloseToastMessage: () => void;
  openUpgradeModal: () => void
  onChangeSubscription: (
    tier: TIER | null, interval: BILLING_INTERVAL | null, quantity: number | null,
    newPromoCode: string | null, isPreview: boolean, prorationDate: string | null
  ) => Promise<SubscriptionDetails | undefined>
  onLogout: (global?: boolean) => void;
}

const Settings = ({
  subPage, subTab, user, organization, paymentButtonDisabled, paymentDialogError, paymentDialogMessage, 
  paymentRedirectDialogOpen, toastMessage, onCloseToastMessage, onChangeSubscription, clearPaymentDialogError,
  onLogout,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMyAccountPage = location.pathname.startsWith(PAGE_URL.MY_ACCOUNT);
  const activeSubPage = subPage || (isMyAccountPage ? 'my-account-home' : 'settings-home');
  const handleOpenMenu = () => window.dispatchEvent(CAB_UI_EVENT.NAV_OPEN);

  const accountNavItem: SettingsNavItem = {
    label: 'Account Management',
    title: 'Account Management',
    description: 'Update your profile, organization, users, subscription, and logout settings.',
    path: PAGE_URL.MY_ACCOUNT,
    key: 'my-account',
    children: [
      {
        label: 'My Profile',
        description: 'Update your name, profile details, and personal account settings.',
        path: PAGE_URL.ACCOUNT,
        pageKey: 'profile',
      },
      ...(!organization ? [] : teamSubTabs),
      {
        label: 'Log Out',
        description: 'Choose whether to log out on this device or all devices.',
        path: `${PAGE_URL.MY_ACCOUNT}/logout`,
        pageKey: 'logout',
      },
    ],
  };

  const settingsNavItems: SettingsNavItem[] = [
    {
      label: 'Calendar Setup',
      title: 'Calendar Setup',
      description: 'Attach calendars to your leaders in Cabinet and adjust calendar colors.',
      path: `${PAGE_URL.SETTINGS}/calendar-settings`,
      key: 'calendar-settings',
    },
    {
      label: 'Scheduling',
      title: 'Scheduling',
      description: 'Set meeting defaults, calendar display, locations, templates, and notifications.',
      path: `${PAGE_URL.SETTINGS}/scheduling`,
      key: 'scheduling',
      children: schedulingSubTabs,
    },
    {
      label: 'Integrations',
      title: 'Integrations',
      description: 'Connect calendar providers, Zoom, and other services Cabinet uses.',
      path: `${PAGE_URL.SETTINGS}/integrations`,
      key: 'integrations',
    },
    {
      label: (
        <NavLabelContent>
          Reports
          {(!user?.features.CALENDAR_ANALYTICS) && (
            <CabIcon Icon={IoLockClosedOutline} size="small" />
          )}
        </NavLabelContent>
      ),
      title: 'Reports',
      description: 'Manage personal and organization reporting settings.',
      path: PAGE_URL.ANALYTICS_TAB,
      key: 'analytics',
      disabled: !user?.features.CALENDAR_ANALYTICS,
      hidden: user?.features.BLOCK_CALENDAR_ANALYTICS,
      children: personalOrganizationSubTabs,
    },
    {
      label: (
        <NavLabelContent>
          CRM
          {(!hasCRM(user)) && (
            <CabIcon Icon={IoLockClosedOutline} size="small" />
          )}
        </NavLabelContent>
      ),
      title: 'CRM',
      description: 'Manage personal and organization CRM labels and properties.',
      path: PAGE_URL.CRM_SETTINGS,
      key: 'crm',
      disabled: !hasCRM(user),
      hidden: !hasCRM(user),
      children: crmSubTabs,
    },
  ];
  const visibleSettingsNavItems = (isMyAccountPage ? [accountNavItem] : settingsNavItems)
    .filter(item => !item.hidden);

  const isItemActive = (item: SettingsNavItem) => activeSubPage === item.key
    || !!item.children?.some(child => {
      const pageKey = child.pageKey || item.key;
      return activeSubPage === pageKey && (child.subTab ? subTab === child.subTab : !subTab);
    });

  const activeSettingsItem = visibleSettingsNavItems.find(isItemActive);
  const activeChildTab = activeSettingsItem?.children?.find(child => {
    const pageKey = child.pageKey || activeSettingsItem.key;
    return activeSubPage === pageKey && (child.subTab ? subTab === child.subTab : !subTab);
  });
  const shouldShowSectionTabs = !!activeSettingsItem?.children
    && activeSettingsItem.key !== 'scheduling'
    && activeSettingsItem.key !== 'my-account';

  const navigateToSettingsItem = (item: SettingsNavItem) => {
    if (item.disabled) return;
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    } else if (item.children?.[0]?.path) {
      navigate(item.children[0].path);
    }
  };

  const renderSettingsContent = () => {
    const schedulingTitle = schedulingTitleBySubTab[subTab || ''] || 'Meeting Defaults';
    const schedulingDescription = schedulingDescriptionBySubTab[subTab || '']
      || (!subTab
        ? 'Update the default meetings settings that appear when proposing times across all meeting types and ' +
          'executives.'
        : undefined);
    const personalOrganizationTitle = subTab === 'org' ? 'Organization' : 'Personal';
    const teamTitle = teamTitleBySubTab[subTab || ''] || 'Organization Details';

    switch (activeSubPage) {
      case 'settings-home':
        return (
          <SettingsHome
            items={visibleSettingsNavItems}
            onSelect={navigateToSettingsItem}
          />
        );
      case 'scheduling':
        return (
          <SettingsOptionPage
            item={settingsNavItems.find(item => item.key === 'scheduling')}
            onSelect={child => navigate(child.path)}
          />
        );
      case 'my-account-home':
        return (
          <SettingsOptionPage
            item={accountNavItem}
            onSelect={child => navigate(child.path)}
          />
        );
      case 'calendar-settings':
        return (
          <SettingsPageFrame title="Calendar Setup">
            <ManageCalendars embedded />
          </SettingsPageFrame>
        );
      case 'integrations':
        return (
          <SettingsPageFrame title="Integrations">
            <IntegrationSettings embedded />
          </SettingsPageFrame>
        );
      case 'analytics':
        return (
          <SettingsPageFrame title={personalOrganizationTitle}>
            <AnalyticsTabContainer subTab={subPage === 'analytics' ? subTab : undefined} />
          </SettingsPageFrame>
        );
      case 'crm':
        return (
          <SettingsPageFrame title={personalOrganizationTitle}>
            <CRMSettingsContainer subTab={subPage === 'crm' ? subTab : undefined} />
          </SettingsPageFrame>
        );
      case 'support-map':
        return (
          <SettingsPageFrame title="Support Map">
            <SupportMapContainer embedded />
          </SettingsPageFrame>
        );
      case 'team':
        return (
          <SettingsPageFrame title={teamTitle}>
            <Subscription
              subTab={subPage === 'team' ? subTab : undefined}
              onChangeSubscription={onChangeSubscription}
              clearPaymentDialogError={clearPaymentDialogError}
              paymentButtonDisabled={paymentButtonDisabled}
              paymentDialogError={paymentDialogError}
              paymentDialogMessage={paymentDialogMessage}
              paymentRedirectDialogOpen={paymentRedirectDialogOpen}
            />
          </SettingsPageFrame>
        );
      case 'profile':
        return (
          <SettingsPageFrame title="My Profile">
            <Account />
          </SettingsPageFrame>
        );
      case 'logout':
        return (
          <SettingsPageFrame title="Log Out">
            <LogoutSettings onLogout={onLogout} />
          </SettingsPageFrame>
        );
      default:
        return (
          <SettingsPageFrame title={schedulingTitle} description={schedulingDescription}>
            <UserPreferences schedulingSubTab={subPage === 'preferences' ? subTab : undefined} />
          </SettingsPageFrame>
        );
    }
  };

  return (
    <CabinetPage pageName={isMyAccountPage ? 'My Account' : 'Settings'} noHeader>
      <SettingsLayout>
        <SettingsTopBar>
          <SettingsMenuButton aria-label="Open navigation" onClick={handleOpenMenu}>
            <CabIcon Icon={IoMenu} />
          </SettingsMenuButton>
        </SettingsTopBar>
        <SettingsContent>
          {activeSettingsItem && activeSubPage !== 'my-account-home' && (
            <SettingsSectionHeader>
              <SettingsBreadcrumb aria-label={isMyAccountPage ? 'My Account breadcrumb' : 'Settings breadcrumb'}>
                <SettingsBreadcrumbButton
                  type="button"
                  onClick={() => navigate(isMyAccountPage ? PAGE_URL.MY_ACCOUNT : PAGE_URL.SETTINGS)}
                >
                  {isMyAccountPage ? 'My Account' : 'Settings'}
                </SettingsBreadcrumbButton>
                <SettingsBreadcrumbSeparator aria-hidden="true">
                  ›
                </SettingsBreadcrumbSeparator>
                {activeChildTab ? (
                  <>
                    {!isMyAccountPage && (
                      <>
                        <SettingsBreadcrumbButton
                          type="button"
                          onClick={() => activeSettingsItem.path && navigate(activeSettingsItem.path)}
                        >
                          {activeSettingsItem.title}
                        </SettingsBreadcrumbButton>
                        <SettingsBreadcrumbSeparator aria-hidden="true">
                          ›
                        </SettingsBreadcrumbSeparator>
                      </>
                    )}
                    <SettingsBreadcrumbCurrent>
                      {activeChildTab.label}
                    </SettingsBreadcrumbCurrent>
                  </>
                ) : (
                  <SettingsBreadcrumbCurrent>
                    {activeSettingsItem.title}
                  </SettingsBreadcrumbCurrent>
                )}
              </SettingsBreadcrumb>
              {shouldShowSectionTabs && (
                <SettingsSectionTabs aria-label={`${activeSettingsItem.title} settings`}>
                  {activeSettingsItem.children?.map(child => {
                    const pageKey = child.pageKey || activeSettingsItem.key;
                    const isActiveChild = activeSubPage === pageKey
                      && (child.subTab ? subTab === child.subTab : !subTab);

                    return (
                      <SettingsSubNavButton
                        type="button"
                        key={child.path}
                        onClick={() => navigate(child.path)}
                        aria-current={isActiveChild ? 'page' : undefined}
                        active={isActiveChild}
                      >
                        <SettingsSubNavButtonLabel active={isActiveChild}>
                          {child.label}
                        </SettingsSubNavButtonLabel>
                      </SettingsSubNavButton>
                    );
                  })}
                </SettingsSectionTabs>
              )}
            </SettingsSectionHeader>
          )}
          {renderSettingsContent()}
        </SettingsContent>
      </SettingsLayout>
      <Snackbar
        open={!!toastMessage}
        onClose={onCloseToastMessage}
        autoHideDuration={5000}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          marginTop: "64px",
        }}
      />
    </CabinetPage>
  );
};

export default Settings;

const SettingsHome = ({
  items,
  onSelect,
}: {
  items: SettingsNavItem[];
  onSelect: (item: SettingsNavItem) => void;
}) => (
  <SettingsPageFrame title="Settings">
    <SettingsHomeList>
      {items.map(item => (
        <SettingsHomeRow
          key={item.key}
          type="button"
          disabled={item.disabled}
          onClick={() => onSelect(item)}
        >
          <span>
            <SettingsHomeRowTitle variant="h2">
              {item.label}
            </SettingsHomeRowTitle>
            <SettingsHomeRowDescription>
              {item.description}
            </SettingsHomeRowDescription>
          </span>
          <SettingsHomeRowArrow aria-hidden="true">
            ›
          </SettingsHomeRowArrow>
        </SettingsHomeRow>
      ))}
    </SettingsHomeList>
  </SettingsPageFrame>
);

const SettingsOptionPage = ({
  item,
  onSelect,
}: {
  item: SettingsNavItem | undefined;
  onSelect: (child: SettingsChildTab) => void;
}) => {
  if (!item) return null;

  return (
    <SettingsPageFrame title={item.title} description={item.description}>
      <SettingsHomeList>
        {item.children?.map(child => (
          <SettingsHomeRow
            key={child.path}
            type="button"
            onClick={() => onSelect(child)}
          >
            <span>
              <SettingsHomeRowTitle variant="h2">
                {child.label}
              </SettingsHomeRowTitle>
              {child.description && (
                <SettingsHomeRowDescription>
                  {child.description}
                </SettingsHomeRowDescription>
              )}
            </span>
            <SettingsHomeRowArrow aria-hidden="true">
              ›
            </SettingsHomeRowArrow>
          </SettingsHomeRow>
        ))}
      </SettingsHomeList>
    </SettingsPageFrame>
  );
};

const SettingsPageFrame = ({
  title, description, children
}: {
  title?: string;
  description?: string;
  children: ReactNode
}) => (
  <PaddedSettingsContent>
    {title && (
      <SettingsPageTitle variant="h1">
        {title}
      </SettingsPageTitle>
    )}
    {description && (
      <SettingsPageDescription variant="body2" color="text.secondary">
        {description}
      </SettingsPageDescription>
    )}
    {children}
  </PaddedSettingsContent>
);

const LogoutSettings = ({ onLogout }: { onLogout: (global?: boolean) => void }) => (
  <LogoutContent>
    <LogoutActions>
      <CabButton buttonType="secondary" onClick={() => onLogout(false)}>
        Log out on this device only
      </CabButton>
      <CabButton color="primary" onClick={() => onLogout(true)}>
        Log out on all devices
      </CabButton>
    </LogoutActions>
  </LogoutContent>
);
